import { Coordinate, TileSize } from "../../models"
import { BODY_GROUND_OFFSET } from "../../lpc/body_placement"
import { TileBlockingRect } from "../../tile_blocking_geometry"

/** Each rectangle stays within one roof tile. */
export interface RoofInteriorRect { left: number; top: number; right: number; bottom: number }
export interface RoofWall { x: number; y: number; profiles: TileBlockingRect[] }
const cellKey = (x: number, y: number) => `${x},${y}`
const indexes = new WeakMap<RoofInteriorRect[], Map<string, RoofInteriorRect>>()

export const withinRoofInterior = (rects: RoofInteriorRect[] | undefined, feet: Coordinate): boolean => {
    if (!rects) return true
    let index = indexes.get(rects)
    if (!index) {
        index = new Map(rects.map(rect => [cellKey(Math.floor(rect.left / TileSize), Math.floor(rect.top / TileSize)), rect]))
        indexes.set(rects, index)
    }
    const rect = index.get(cellKey(Math.floor(feet.x / TileSize), Math.floor(feet.y / TileSize)))
    return !!rect && feet.x >= rect.left && feet.x < rect.right && feet.y >= rect.top && feet.y < rect.bottom
}

/** Local heuristic: trim roof perimeter tiles at their wall's inner face.
 * Gameplay contract and limitations: docs/occlusion-rules.md.
 * Gaps between posts inherit the neighboring wall line as a doorway threshold.
 * This does not attempt to discover enclosed rooms or fill arbitrary wall gaps.
 * Work is proportional to roof tiles plus sorting perimeter runs. */
export const buildRoofInterior = (roof: Coordinate[], walls: RoofWall[]): RoofInteriorRect[] | undefined => {
    if (!roof.length || !walls.length) return undefined
    const bounds = new Map(roof.map(({ x, y }) => [cellKey(x, y), {
        left: x * TileSize, right: (x + 1) * TileSize, top: y * TileSize, bottom: (y + 1) * TileSize,
    }]))
    const wallByCell = new Map(walls.map(wall => [cellKey(wall.x, wall.y), wall]))
    let trimmed = false
    for (const side of ["north", "south", "west", "east"] as const) {
        const horizontal = side === "north" || side === "south"
        const outward = side === "north" || side === "west" ? -1 : 1
        const groups = new Map<number, Coordinate[]>()
        for (const cell of roof) {
            if (bounds.has(cellKey(cell.x + (horizontal ? 0 : outward), cell.y + (horizontal ? outward : 0)))) continue
            const row = horizontal ? cell.y : cell.x
            const group = groups.get(row) ?? []
            group.push(cell); groups.set(row, group)
        }
        const along = (cell: Coordinate) => horizontal ? cell.x : cell.y
        for (const group of groups.values()) {
            group.sort((a, b) => along(a) - along(b))
            const runs: Coordinate[][] = []
            for (const cell of group) {
                const last = runs[runs.length - 1]
                if (last && along(cell) === along(last[last.length - 1]) + 1) last.push(cell)
                else runs.push([cell])
            }
            for (const run of runs) {
                const posts = run.map(cell => wallByCell.get(cellKey(cell.x, cell.y)))
                const guides = posts.map(post => post?.profiles.find(rect => horizontal ? rect.width > rect.height : rect.height > rect.width))
                // Nearest posts/guides on each side, found in linear passes.
                const leftPosts: number[] = [], rightPosts: number[] = [], leftGuides: number[] = [], rightGuides: number[] = []
                let post = -1, guide = -1
                for (let i = 0; i < run.length; i++) {
                    if (posts[i]) post = i
                    if (guides[i]) guide = i
                    leftPosts[i] = post; leftGuides[i] = guide
                }
                post = guide = -1
                for (let i = run.length - 1; i >= 0; i--) {
                    if (posts[i]) post = i
                    if (guides[i]) guide = i
                    rightPosts[i] = post; rightGuides[i] = guide
                }
                for (let i = 0; i < run.length; i++) {
                    // An open end has no implied doorway boundary.
                    if (!posts[i] && (leftPosts[i] < 0 || rightPosts[i] < 0)) continue
                    const left = leftGuides[i], right = rightGuides[i]
                    const nearest = left < 0 ? right : right < 0 || i - left <= right - i ? left : right
                    let profile = nearest < 0 ? undefined : guides[nearest]
                    if (!profile) {
                        profile = (posts[i] ?? posts[leftPosts[i]])?.profiles[0]
                        if (!profile) continue
                        // A corner end post has no run-facing profile; its actual
                        // thickness supplies the short perpendicular wall line.
                        const thickness = Math.min(profile.width, profile.height)
                        profile = { ...profile, width: horizontal ? profile.width : thickness,
                            height: horizontal ? thickness : profile.height }
                    }
                    const cell = run[i]
                    const base = (horizontal ? cell.y : cell.x) * TileSize
                    const low = base + (horizontal ? profile.y + BODY_GROUND_OFFSET : profile.x)
                    const high = low + (horizontal ? profile.height : profile.width)
                    const boundary = outward < 0 ? high : low
                    // The feet offset can carry a north boundary into the next
                    // tile. Trim that tile too, stopping once the line is passed.
                    for (let step = 0; ; step++) {
                        const x = cell.x - (horizontal ? 0 : outward * step)
                        const y = cell.y - (horizontal ? outward * step : 0)
                        const rect = bounds.get(cellKey(x, y))
                        if (!rect) break
                        const near = horizontal ? outward < 0 ? rect.top : rect.bottom : outward < 0 ? rect.left : rect.right
                        if (outward < 0 ? near >= boundary : near <= boundary) break
                        if (side === "north") rect.top = Math.max(rect.top, boundary)
                        if (side === "south") rect.bottom = Math.min(rect.bottom, boundary)
                        if (side === "west") rect.left = Math.max(rect.left, boundary)
                        if (side === "east") rect.right = Math.min(rect.right, boundary)
                        trimmed = true
                    }
                }
            }
        }
    }
    return trimmed ? [...bounds.values()].filter(rect => rect.left < rect.right && rect.top < rect.bottom) : undefined
}
